/*
 * xml-scan.js — a streaming XML tokenizer for GPS formats (GPX, KML, TCX).
 *
 *   GpsXmlScan.scan(text, h)
 *     h.open(local, attrs, qname)  — attrs is a plain object keyed by LOCAL name
 *     h.close(local, qname)
 *     h.text(str)                  — only called while h.cap is truthy
 *
 * Why not DOMParser: it does not exist in a Web Worker, and a DOM of a 50 MB
 * GPX (≈1 M track points, 6–10 nodes each) costs hundreds of MB before a
 * single coordinate is read. The formats we read are shallow and regular; a
 * forward scan over the string with indexOf is O(n), allocates only the text
 * the parser asked for, and runs in the worker.
 *
 * Names are reported by LOCAL name (after the namespace prefix): GPX files in
 * the wild spell the same Garmin extension as gpxtpx:hr, ns3:hr, gpxdata:hr or
 * plain hr, and the parsers want «hr». The qualified name is passed too for
 * the rare place where the prefix matters.
 */
(function () {
    'use strict';
    var root = typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : this);

    var ENT = { lt: '<', gt: '>', amp: '&', quot: '"', apos: "'" };

    function decode(s) {
        if (s.indexOf('&') < 0) return s;
        return s.replace(/&(#x[0-9a-fA-F]+|#[0-9]+|[a-zA-Z]+);/g, function (m, e) {
            if (e.charCodeAt(0) === 35) {
                var cp = e.charCodeAt(1) === 120 || e.charCodeAt(1) === 88 ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
                if (!isFinite(cp) || cp < 0 || cp > 0x10FFFF) return m;
                try { return String.fromCodePoint(cp); } catch (_) { return m; }
            }
            return Object.prototype.hasOwnProperty.call(ENT, e) ? ENT[e] : m;
        });
    }

    function local(q) {
        var c = q.indexOf(':');
        return c < 0 ? q : q.slice(c + 1);
    }

    var ATTR = /([^\s=\/>]+)\s*=\s*("([^"]*)"|'([^']*)')/g;

    function attrsOf(s) {
        var out = {};
        if (!s) return out;
        ATTR.lastIndex = 0;
        var m;
        while ((m = ATTR.exec(s))) {
            var v = m[3] !== undefined ? m[3] : m[4];
            out[local(m[1])] = decode(v);
        }
        return out;
    }

    function scan(text, h) {
        var n = text.length, i = 0;
        while (i < n) {
            var lt = text.indexOf('<', i);
            if (lt < 0) {
                if (h.cap && i < n) h.text(decode(text.slice(i)));
                break;
            }
            if (lt > i && h.cap) h.text(decode(text.slice(i, lt)));
            var c = text.charCodeAt(lt + 1);
            if (c === 33) { // '!'
                if (text.startsWith('<!--', lt)) {
                    var ce = text.indexOf('-->', lt + 4);
                    i = ce < 0 ? n : ce + 3;
                    continue;
                }
                if (text.startsWith('<![CDATA[', lt)) {
                    var de = text.indexOf(']]>', lt + 9);
                    if (h.cap) h.text(text.slice(lt + 9, de < 0 ? n : de));
                    i = de < 0 ? n : de + 3;
                    continue;
                }
                // DOCTYPE / other declarations: skip to the matching '>' (no internal subset support needed)
                var dg = text.indexOf('>', lt + 2);
                i = dg < 0 ? n : dg + 1;
                continue;
            }
            if (c === 63) { // '?'
                var pe = text.indexOf('?>', lt + 2);
                i = pe < 0 ? n : pe + 2;
                continue;
            }
            // find the end of the tag, honouring quotes (a '>' may sit inside an attribute value)
            var j = lt + 1, q = 0;
            while (j < n) {
                var ch = text.charCodeAt(j);
                if (q) { if (ch === q) q = 0; }
                else if (ch === 34 || ch === 39) q = ch;
                else if (ch === 62) break;
                j++;
            }
            if (j >= n) break; // truncated file: stop at the last whole tag
            if (c === 47) { // '/' closing tag
                var qn = text.slice(lt + 2, j).trim();
                h.close(local(qn), qn);
            } else {
                var selfClose = text.charCodeAt(j - 1) === 47;
                var body = text.slice(lt + 1, selfClose ? j - 1 : j);
                var sp = body.search(/\s/);
                var qname = sp < 0 ? body : body.slice(0, sp);
                var ln = local(qname);
                h.open(ln, sp < 0 ? {} : attrsOf(body.slice(sp)), qname);
                if (selfClose) h.close(ln, qname);
            }
            i = j + 1;
        }
    }

    /*
     * Text decoding. GPX/KML are XML: a BOM or the prolog's encoding="" decides.
     * Russian navigators and some Windows exporters still write windows-1251
     * GPX with the prolog saying so; decoding those as UTF-8 turns every
     * waypoint name into mojibake.
     */
    function decodeBytes(u8) {
        if (u8.length >= 2 && u8[0] === 0xFF && u8[1] === 0xFE) return new TextDecoder('utf-16le').decode(u8.subarray(2));
        if (u8.length >= 2 && u8[0] === 0xFE && u8[1] === 0xFF) return new TextDecoder('utf-16be').decode(u8.subarray(2));
        if (u8.length >= 3 && u8[0] === 0xEF && u8[1] === 0xBB && u8[2] === 0xBF) return new TextDecoder('utf-8').decode(u8.subarray(3));
        // UTF-16 without BOM: '<' followed by a zero byte
        if (u8.length >= 4 && u8[0] === 0x3C && u8[1] === 0 && u8[2] !== 0) return new TextDecoder('utf-16le').decode(u8);
        if (u8.length >= 4 && u8[0] === 0 && u8[1] === 0x3C) return new TextDecoder('utf-16be').decode(u8);
        var head = '';
        var lim = Math.min(u8.length, 200);
        for (var k = 0; k < lim; k++) head += String.fromCharCode(u8[k]);
        var m = /^\s*<\?xml[^>]*encoding\s*=\s*["']([^"']+)["']/i.exec(head);
        var label = m ? m[1].toLowerCase() : 'utf-8';
        if (label === 'utf-8' || label === 'utf8') {
            // Declared UTF-8 but not valid UTF-8 (a hand-edited file saved as ANSI): fall back.
            try { return new TextDecoder('utf-8', { fatal: true }).decode(u8); }
            catch (_) { return new TextDecoder(guessLegacy(u8)).decode(u8); }
        }
        try { return new TextDecoder(label).decode(u8); }
        catch (_) { return new TextDecoder('utf-8').decode(u8); }
    }

    // Cyrillic text in windows-1251 lives in 0xC0–0xFF; Latin-1 accents are sparser there.
    function guessLegacy(u8) {
        var hi = 0, cyr = 0, lim = Math.min(u8.length, 1 << 20);
        for (var k = 0; k < lim; k++) {
            var b = u8[k];
            if (b >= 0x80) { hi++; if (b >= 0xC0) cyr++; }
        }
        return hi > 0 && cyr / hi > 0.8 ? 'windows-1251' : 'windows-1252';
    }

    root.GpsXmlScan = { scan: scan, decodeBytes: decodeBytes, attrsOf: attrsOf, decodeEntities: decode };
})();
